import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * הכרזה על מעבר עמוד באפליקציית SPA - WCAG 4.1.3 / 2.4.3.
 *
 * בניווט רגיל הדפדפן מודיע לקורא המסך שהעמוד התחלף; ב-React Router ה-DOM
 * מתחלף בשקט והמשתמש לא יודע שהוא בעמוד אחר. אזור ה-live כאן מקריא את שם
 * העמוד החדש, והמיקוד מוחזר לתחילת התוכן כדי ש-Tab ימשיך מההתחלה.
 */
const RouteAnnouncer = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    // בטעינה הראשונה הדפדפן כבר הכריז על העמוד בעצמו.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    /*
     * את הכותרת קובעים effects אחרים (App, עמוד התרומה), ואין ערובה שהם
     * כבר רצו כשאנחנו כאן. המתנה על טיימר שרירותי הייתה מרוץ - לפעמים
     * הכרזנו את שם העמוד *הקודם*. במקום זה מאזינים לשינוי בפועל של
     * <title>, ומכריזים ברגע שהוא מתחלף.
     */
    const titleEl = document.querySelector('title');
    if (!titleEl) return;
    const focusAtNavigation = document.activeElement;
    let announced = false;

    const announce = () => {
      if (announced) return;
      announced = true;
      const title = document.title.trim();
      if (title) setMessage(t('accessibility.pageChanged', { title }));
      const main = document.getElementById('main-content');
      const active = document.activeElement;
      // A delayed title/fallback must not steal focus after the user has already
      // chosen a table cell, filter, or another control on the new page.
      if (active === document.body || (active === focusAtNavigation && !main?.contains(active))) {
        main?.focus({ preventScroll: true });
      }
    };

    const observer = new MutationObserver(() => {
      observer.disconnect();
      announce();
    });
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });

    /*
     * רשת ביטחון: יש מסלולים שכלל לא משנים את הכותרת, ואז ה-observer לא
     * יורה לעולם. אחרי חלון קצר מכריזים את מה שיש - עדיף שם עמוד ישן
     * מאשר דממה מוחלטת אחרי ניווט.
     */
    const fallback = window.setTimeout(() => {
      observer.disconnect();
      announce();
    }, 500);

    return () => {
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [pathname, t]);

  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {message}
    </div>
  );
};

export default RouteAnnouncer;
